// The one place a chat link is built and read. Web routes, the mobile deep
// link handler, push taps, bell rows, search hits and "copy link" all go
// through here, so the shape of a chat URL is decided once.
//
//   /chat                                  your chats
//   /chat/<conversationId>                 one chat
//   /chat/<conversationId>/m/<messageId>   that chat, opened at one message
//   /chat/s/<spaceSlug>                    that space's own chat
//   /chat/s/<spaceSlug>/f/<factiiiId>      that factiii's chat
//
// The same paths arrive three ways: as a path, as an https URL on the site,
// and under the app scheme (`factiii://chat/<conversationId>/m/<messageId>`).

export const CHAT_ROUTE = '/chat';

/** The mobile app's URL scheme (`scheme` in apps/factiii-mobile/app.json). */
export const APP_SCHEME = 'factiii';

const ROOT_SEGMENT = 'chat';
const SPACE_SEGMENT = 's';
const FACTIII_SEGMENT = 'f';
const MESSAGE_SEGMENT = 'm';

export type ChatLinkTarget =
  /** `/chat` itself: the conversation list. */
  | { kind: 'home' }
  /** One conversation, optionally opened at a message. */
  | { kind: 'conversation'; conversationId: string; messageId: number | null }
  /** The chat a space or one of its factiiis owns. It may have no id yet. */
  | { kind: 'space'; spaceSlug: string; factiiiId: number | null };

/** A whole positive number, or null — ids never arrive as anything else. */
const toId = (raw: string | undefined): number | null => {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

/** The path of a chat link, starting at `/chat`. */
export const buildChatLink = (
  target:
    | { conversationId: string; messageId?: number | null }
    | { spaceSlug: string; factiiiId?: number | null }
): string => {
  if ('spaceSlug' in target) {
    const base = `${CHAT_ROUTE}/${SPACE_SEGMENT}/${encodeURIComponent(target.spaceSlug)}`;
    return target.factiiiId
      ? `${base}/${FACTIII_SEGMENT}/${target.factiiiId}`
      : base;
  }
  const base = `${CHAT_ROUTE}/${encodeURIComponent(target.conversationId)}`;
  return target.messageId
    ? `${base}/${MESSAGE_SEGMENT}/${target.messageId}`
    : base;
};

/** The same link as an absolute URL on `siteUrl`, for copying and sharing. */
export const buildChatUrl = (
  siteUrl: string,
  target: Parameters<typeof buildChatLink>[0]
): string => `${siteUrl.replace(/\/+$/, '')}${buildChatLink(target)}`;

/**
 * What the path segments AFTER `/chat` point at — the shape a catch-all route
 * hands over. An id that is not a number is dropped rather than refused: the
 * chat still opens, at the newest message.
 */
export const parseChatSegments = (segments: string[]): ChatLinkTarget => {
  const [first, second, third, fourth] = segments;
  if (!first) return { kind: 'home' };
  if (first === SPACE_SEGMENT) {
    if (!second) return { kind: 'home' };
    return {
      kind: 'space',
      spaceSlug: second,
      factiiiId: third === FACTIII_SEGMENT ? toId(fourth) : null
    };
  }
  return {
    kind: 'conversation',
    conversationId: first,
    messageId: second === MESSAGE_SEGMENT ? toId(third) : null
  };
};

const splitPath = (path: string): string[] =>
  path
    .split(/[?#]/)[0]
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    });

/**
 * Read a chat link in any of its three forms. Null for anything that is not a
 * chat link. The host of an https link is NOT checked here: a caller that
 * takes URLs from outside decides which hosts it trusts (the OS already does
 * for a universal link; pasted content goes through `internalPath` first).
 */
export const parseChatLink = (pathOrUrl: string): ChatLinkTarget | null => {
  const input = pathOrUrl.trim();
  if (!input) return null;

  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(input);
  if (!scheme) {
    const segments = splitPath(input);
    return segments[0] === ROOT_SEGMENT
      ? parseChatSegments(segments.slice(1))
      : null;
  }

  // Everything after `scheme://`. Under the app scheme there is no host, so
  // `chat` is the first segment; under http(s) the first segment is the host.
  const segments = splitPath(input.slice(scheme[0].length));
  const protocol = scheme[1].toLowerCase();
  const path =
    protocol === APP_SCHEME
      ? segments
      : protocol === 'http' || protocol === 'https'
        ? segments.slice(1)
        : null;
  if (!path || path[0] !== ROOT_SEGMENT) return null;
  return parseChatSegments(path.slice(1));
};
